import { useState, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Autocomplete,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import { createFilterOptions } from '@mui/material/useAutocomplete';
import { useTranslation } from '../../common/components/LocalizationProvider';
import { usePremiumTokens } from '../../common/theme/usePremiumTokens';

const AddAttributeDialog = ({ open, onResult, definitions }) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const filter = createFilterOptions({
    stringify: (option) => (typeof option === 'object' ? `${option.name} ${option.key || ''}` : option),
  });

  const options = useMemo(
    () =>
      Object.entries(definitions)
        .map(([key, value]) => ({
          key,
          name: value.name || key,
          type: value.type,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [definitions],
  );

  const [key, setKey] = useState();
  const [type, setType] = useState('string');

  const handleAdd = () => {
    document.activeElement?.blur();
    onResult({ key, type });
  };

  const handleCancel = () => {
    document.activeElement?.blur();
    onResult(null);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="xs"
      disableEnforceFocus
      disableRestoreFocus
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(22, 27, 34, 0.75)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: tokens?.radius?.xl || '16px',
          border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.1)'}`,
          boxShadow: tokens?.shadows?.xl || '0 12px 32px rgba(0, 0, 0, 0.6)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
          borderBottom: `1px solid ${tokens.border.subtle}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon sx={{ color: tokens.accent.primary }} />
          <span style={{ color: tokens.text.primary, fontWeight: 600 }}>
            {t('sharedAdd')} {t('sharedAttribute')}
          </span>
        </Box>
        <IconButton
          size="small"
          onClick={handleCancel}
          sx={{
            color: tokens.text.secondary,
            '&:hover': {
              color: tokens.semantic.error,
              backgroundColor: tokens.semantic.errorBg,
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
          pt: 3,
          pb: 2,
        }}
      >
        <Autocomplete
          freeSolo
          onChange={(_, option) => {
            setKey(
              option && typeof option === 'object'
                ? option.key ?? option.inputValue
                : option,
            );
            if (option && (option.type || option.inputValue)) {
              setType(option.type);
            }
          }}
          filterOptions={(options, params) => {
            const filtered = filter(options, params);
            if (
              params.inputValue &&
              !options.some(
                (x) =>
                  (typeof x === 'object' ? x.key : x) === params.inputValue,
              )
            ) {
              filtered.push({
                inputValue: params.inputValue,
                name: `${t('sharedAdd')} "${params.inputValue}"`,
              });
            }
            return filtered;
          }}
          options={options}
          getOptionLabel={(option) =>
            option && typeof option === 'object'
              ? option.inputValue || option.name
              : option
          }
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            return (
              <li
                key={key}
                {...rest}
                style={{
                  padding: '10px 16px',
                  color: tokens.text.primary,
                  transition: 'background-color 0.15s ease',
                }}
              >
                {option.name || option}
              </li>
            );
          }}
          componentsProps={{
            paper: {
              sx: {
                backgroundColor: 'rgba(22, 27, 34, 0.75)',
                backdropFilter: 'blur(16px)',
                WebkitBackdropFilter: 'blur(16px)',
                borderRadius: tokens?.radius?.lg || '12px',
                border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.1)'}`,
                mt: 0.5,
                '& .MuiAutocomplete-listbox': {
                  '& .MuiAutocomplete-option': {
                    '&:hover': {
                      backgroundColor: tokens?.accent?.subtle || 'rgba(0, 212, 170, 0.1)',
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: tokens?.accent?.hover || 'rgba(0, 212, 170, 0.15)',
                    },
                  },
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('sharedAttribute')}
              className="premium-input"
            />
          )}
        />
        <FormControl fullWidth size="small" disabled={key in definitions} className="premium-input">
          <InputLabel>{t('sharedType')}</InputLabel>
          <Select
            label={t('sharedType')}
            value={type || 'string'}
            onChange={(e) => setType(e.target.value)}
          >
            <MenuItem value="string">{t('sharedTypeString')}</MenuItem>
            <MenuItem value="number">{t('sharedTypeNumber')}</MenuItem>
            <MenuItem value="boolean">{t('sharedTypeBoolean')}</MenuItem>
          </Select>
        </FormControl>
      </DialogContent>

      {/* Actions */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${tokens.border.subtle}`,
          gap: 1,
        }}
      >
        <Button
          onClick={handleCancel}
          sx={{
            color: tokens.text.secondary,
            borderRadius: tokens.radius.md,
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            '&:hover': {
              backgroundColor: tokens.surface.glass,
              color: tokens.text.primary,
            },
          }}
        >
          {t('sharedCancel')}
        </Button>
        <Button
          variant="contained"
          disabled={!key}
          onClick={handleAdd}
          startIcon={<AddIcon />}
          sx={{
            bgcolor: tokens.accent.primary,
            color: tokens.isDark ? tokens.text.inverse : '#ffffff',
            borderRadius: tokens.radius.md,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            boxShadow: 'none',
            '&:hover': {
              bgcolor: tokens.isDark ? '#00e6b8' : '#009973',
              boxShadow: tokens.shadows.glowAccent,
            },
            '&.Mui-disabled': {
              bgcolor: tokens.surface.glass,
              color: tokens.text.muted,
            },
          }}
        >
          {t('sharedAdd')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAttributeDialog;
